import React from "react";
import { Box, ButtonBase, Stack, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";

export interface TagFilterBarProps {
    /** Values used for state/filtering */
    tags: string[];
    /** Labels shown to the user; index-aligned with `tags` */
    displayTags?: string[];
    /** Currently selected value (from `tags`) */
    selectedTag: string;
    /** Returns the selected value (from `tags`) */
    onSelectTag: (tag: string) => void;
}

const TagFilterBar: React.FC<TagFilterBarProps> = ({
    tags,
    displayTags,
    selectedTag,
    onSelectTag,
}) => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;

    return (
        <Stack
            direction="row"
            alignItems="center"
            width="100%"
            height="45px"
            minHeight="45px"
            maxHeight="45px"
            spacing={1}
            sx={{
                overflowX: "auto",
                whiteSpace: "nowrap",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
            }}
        >
            <Box />
            {tags.map((tag, index) => {
                const label = displayTags?.[index] ?? tag;
                const selected = selectedTag === tag;
                return (
                    <ButtonBase
                        key={tag}
                        onClick={() => onSelectTag(tag)}
                        sx={{
                            height: 36,
                            borderRadius: "18px",
                            px: 2,
                            bgcolor: selected
                                ? primary
                                : alpha(primary, 0.06),
                            color: selected ? "white" : "text.primary",
                            boxShadow: selected
                                ? `0 2px 8px ${alpha(primary, 0.25)}`
                                : "none",
                            transition:
                                "background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease",
                            flexShrink: 0,
                        }}
                    >
                        <Typography
                            fontSize="13px"
                            fontWeight={selected ? 600 : 500}
                            lineHeight={1}
                            whiteSpace="nowrap"
                        >
                            {label}
                        </Typography>
                    </ButtonBase>
                );
            })}
            <Box />
        </Stack>
    );
};

export default TagFilterBar;
